import React, {
  useCallback, useState, useMemo, createContext, useContext, FC,
} from 'react';
import { DateString, UseBookingMotivators, BlockedTime } from 'src/common/types/UseBookingMotivators';
import { eachDayOfInterval } from 'date-fns';
import apiFetch from '../../core/fetch';
import captureException from '../utils/captureException';
import { BOOKING_MOTIVATOR_STATE } from '../constants';
import { startOfMonth } from '../../calendar/helpers';
import { noop } from '../helpers';
import { formatDate } from '../utils/dateHelpers';
import useBookingMotivatorsData from './useBookingMotivatorData';

const {
  LIMITED_AVAILABILITY,
} = BOOKING_MOTIVATOR_STATE;

const getBlockedTimes = async (
  boatId: string | undefined,
  date: DateString,
): Promise<BlockedTime[]> => {
  const url = `/boats/${boatId}/blocked-times/?date=${date}`;
  const response = await apiFetch(url);
  if (response.status === 200) {
    return response.json();
  }
  // eslint-disable-next-line @typescript-eslint/no-throw-literal
  throw response;
};

/**
 * Loads the booking motivators for the specified boatId
 */
const useBookingMotivators = (
  boatId?: string,
): UseBookingMotivators => {
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    bookingMotivators,
    setActiveMonth,
    canGoBack,
  } = useBookingMotivatorsData(boatId);

  const loadBlockedTimes = useCallback(async (
    dateToLoad: Date | null,
  ): Promise<void> => {
    if (!dateToLoad) { return; }
    const date = formatDate(dateToLoad);
    const hasBookingMotivator = bookingMotivators[date];
    const hasLimitedAvailability = bookingMotivators[date]?.includes(LIMITED_AVAILABILITY);
    if (!hasBookingMotivator || !hasLimitedAvailability) {
      setBlockedTimes([]);
    }

    setLoading(true);
    try {
      const allBlockedTimesArray = await getBlockedTimes(boatId, date);
      setBlockedTimes(allBlockedTimesArray);
    } catch (error) {
      captureException(error);
      setBlockedTimes([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 700); // Delay of 0.7 seconds
    }
  }, [boatId, bookingMotivators]);

  const handleMonthChange = useCallback(
    (dateObject: Date) => setActiveMonth(
      formatDate(startOfMonth(dateObject)),
    ),
    [setActiveMonth],
  );

  const someUnavailableInRange = useCallback((start: Date, end: Date) => {
    const dates = eachDayOfInterval({ start, end });
    return dates.some((date) => bookingMotivators[formatDate(date)]?.includes('UNAVAILABLE'));
  }, [bookingMotivators]);

  return useMemo(() => ({
    bookingMotivators,
    blockedTimes,
    loading,
    canGoBack,
    loadBlockedTimes,
    handleMonthChange,
    someUnavailableInRange,
  }), [
    bookingMotivators,
    blockedTimes,
    loading,
    canGoBack,
    loadBlockedTimes,
    handleMonthChange,
    someUnavailableInRange,
  ]);
};

const initialContext = {
  bookingMotivators: {},
  blockedTimes: [],
  loading: false,
  canGoBack: true,
  loadBlockedTimes: noop,
  handleMonthChange: noop,
  datesByMotivators: undefined,
  someUnavailableInRange: () => false,
};

const BookingMotivatorContext = createContext<UseBookingMotivators>(initialContext);

const useBookingMotivatorsContext = () => useContext(BookingMotivatorContext);

export const BookingMotivatorsProvider: FC<{ boatId?: string }> = (
  { boatId, children },
) => {
  const bookingMotivators = useBookingMotivators(boatId);
  return (
    <BookingMotivatorContext.Provider value={bookingMotivators}>
      {children}
    </BookingMotivatorContext.Provider>
  );
};

export default useBookingMotivatorsContext;
